import packageJson from '~/../package.json';
import { defaultApiResponseHandler, enumValue, hasResponseCode, enums } from '~/util';

class UserService {
    static events = {
        userLoggedIn: 'UserService.userLoggedIn',
        userUpdated: 'UserService.userUpdated',
        userLoggedOut: 'UserService.userLoggedOut',
        savedSearchDataUpdated: 'UserService.savedSearchDataUpdated',
        filterSelected: 'UserService.filterSelected',
    };

    constructor(props) {
        // Using props and state to match some patterns, but no react magic on these
        this.props = {
            api: props.api,
            auth: props.props.auth,
            client: props.props.client,
            events: props.events,
            alert: props.props.alert,
        };
        this.events = UserService.events;
        this.props.events.init(this.events);
        this.state = {
            user: window.app.user ? window.app.user : {},
            user_roles_map: {
                global: {},
                organization: {},
                macl: {},
            },
            mfa_options: window.app?.two_factor_auth_in_progress?.two_factor_auth
                ? window.app.two_factor_auth_in_progress.two_factor_auth
                : null,
            two_factor_auth_enrollment:
                window.app.two_factor_auth_enrollment_in_progress?.two_factor_auth_enrollment ||
                null,
            reset_endpoint: '/reset-password/reset',
        };

        this.initUserRolesMap();
    }

    /**
     * Creates an easily accessible map on the state for use in
     * checking global/organization roles (`this.state.user_roles_map`)
     */
    initUserRolesMap() {
        const userRolesMap = {
            global: {},
            organization: {},
            macl: {},
        };

        // Add user global/organization roles
        if (this.state.user) {
            if (this.state.user.global_roles) {
                this.state.user.global_roles.forEach((role) => {
                    userRolesMap.global[role] = true;
                });
            }
            if (this.state.user.organization_users) {
                this.state.user.organization_users.forEach((org) => {
                    org.organization_roles.forEach((role) => {
                        userRolesMap.organization[role] = true;
                    });
                });
            }
            if (this.state.user.macl_users) {
                this.state.user.macl_users.forEach((macl) => {
                    macl.macl_roles.forEach((role) => {
                        userRolesMap.macl[role] = true;
                    });
                });
            }
        }

        // Update state
        this.state.user_roles_map = userRolesMap;
    }

    get() {
        return this.state.user;
    }

    getAvailableOrganizations = async (options) => {
        try {
            const result = await this.props.api.call(
                {
                    method: 'get',
                    url: '/api/organizations',
                },
                options
            );

            return result.data['hydra:member'];
        } catch (error) {
            throw defaultApiResponseHandler(error);
        }
    };

    /**
     * @returns {boolean}
     */
    isGlobalAdmin() {
        return Boolean(this.state.user_roles_map.global.ROLE_ADMIN);
    }

    isReportYearAdmin() {
        return Boolean(this.state.user_roles_map.global.ROLE_REPORTYEAR_ADMIN);
    }

    isMACLAdmin() {
        return Boolean(this.state.user_roles_map.global.ROLE_MACL_ADMIN);
    }

    isBannerAdmin() {
        return Boolean(this.state.user_roles_map.global.ROLE_BANNER_ADMIN);
    }

    patientPrintingEnabled() {
        return Boolean(this.state.user_roles_map.global.ROLE_PATIENT_PRINTING);
    }

    isMACLUser() {
        return this.state.user?.access_type === enumValue(['user', 'access_type', 'MACL']);
    }

    isMarketUser() {
        return this.state.user?.access_type === enumValue(['user', 'access_type', 'Market']);
    }

    /**
     * @returns {boolean}
     */
    isOrganizationAdmin() {
        return Boolean(this.state.user_roles_map.organization.ROLE_ORG_ADMIN);
    }

    isAdminInOrg(orgId) {
        return (
            this.isGlobalAdmin() ||
            this.state.user?.organization_users?.some(
                (x) =>
                    x.organization.id === orgId && x.organization_roles.includes('ROLE_ORG_ADMIN')
            )
        );
    }

    login = async (credentials) => {
        try {
            const data = new FormData();
            data.set('_email', credentials.email);
            data.set('_password', credentials.password);
            data.set('_csrf_token', this.props.api.csrf_token);
            data.set('_browser_fingerprint', window.app.browser_fingerprint);
            const result = await this.props.api.call(
                {
                    method: 'post',
                    url: '/login',
                    data,
                },
                { handledErrors: true }
            );
            this.doLogin(result.data.user);

            return true;
        } catch (error) {
            if (hasResponseCode(error, 'EXPIRED_CREDENTIALS_ERROR')) {
                const e = new Error('expired');
                e.code = error.response.data.reset_token;
                throw e;
            }
            if (
                error.response &&
                error.response.data &&
                typeof error.response.data.two_factor_auth !== 'undefined'
            ) {
                this.state.mfa_options = error.response.data.two_factor_auth;
                throw new Error('mfa');
            }
            if (typeof error.response?.data?.two_factor_auth_enrollment !== 'undefined') {
                this.state.two_factor_auth_enrollment =
                    error.response.data.two_factor_auth_enrollment;
                throw new Error('mfa-enrollment');
            }
            throw error;
        }
    };

    doLogin = async (user) => {
        this.state.user = user;
        this.initUserRolesMap();
        this.props.auth.setAuth(true);

        this.props.client.setClient({ ...this.state.user.client });
        this.props.events.publish(this.events.userLoggedIn);
        this.props.events.publish(this.events.userUpdated);
        this.props.alert.initBanners(this.state.user?.banners);
        this.state.mfa_options = null;
        this.state.two_factor_auth_enrollment = null;

        // Check version number
        const response = await this.props.api.call({
            method: 'get',
            url: '/security/status/ping',
        });
        const version = response.headers['x-head-version'];
        if (version !== packageJson.version) {
            // Force refresh browser if version number is mismatched
            window.location.reload(true);
        }
    };

    getMfa() {
        return this.state.mfa_options;
    }

    getMfaEnrollment() {
        return this.state.two_factor_auth_enrollment;
    }

    mfa = async (option) => {
        try {
            await this.props.api.call(
                {
                    method: 'post',
                    url: '/two-factor',
                    data: {
                        _two_factor_auth_option: option,
                        _csrf_token: this.props.api.csrf_token,
                    },
                },
                { handledErrors: true }
            );
            return true;
        } catch (error) {
            if (hasResponseCode(error, 'NOT_AUTHENTICATED_ERROR')) {
                this.props.alert.set('You have been logged out', 'danger', 1);
                this.props.events.publish(this.events.userLoggedOut);
                return true;
            }
            throw new Error(error.response.data.message);
        }
    };

    mfaVerify = async (trusted, code, skipLogin = false) => {
        try {
            const result = await this.props.api.call(
                {
                    method: 'post',
                    url: '/two-factor/check',
                    data: {
                        _trusted_computer: trusted,
                        _two_factor_auth_verification_code: code,
                        _browser_fingerprint: window.app.browser_fingerprint,
                        _csrf_token: this.props.api.csrf_token,
                    },
                },
                { handledErrors: true }
            );
            const { user } = result.data;
            this.state.user = user;
            if (!skipLogin) {
                this.doLogin(user);
            }
            return result;
        } catch (error) {
            if (hasResponseCode(error, 'VERIFICATION_CODE_EXPIRED')) {
                throw new Error(
                    'The authentication code has expired, select Return to request a new code.'
                );
            }
            if (hasResponseCode(error, 'NOT_AUTHENTICATED_ERROR')) {
                this.props.alert.set('You have been logged out', 'danger', 1);
                this.props.events.publish(this.events.userLoggedOut);
                return false;
            }
            throw new Error(error.response.data.message);
        }
    };

    totpVerify = async (code, provider) => {
        try {
            const result = await this.props.api.call(
                {
                    method: 'post',
                    url: '/api/totp-authenticators/verify',
                    data: {
                        code,
                        provider,
                    },
                },
                { handledErrors: true }
            );
            const { user } = result.data;
            this.state.user = user;
            return result;
        } catch (error) {
            if (hasResponseCode(error, 'NOT_AUTHENTICATED_ERROR')) {
                this.props.alert.set('You have been logged out', 'danger', 1);
                this.props.events.publish(this.events.userLoggedOut);
                return false;
            }
            throw new Error(error.response.data.message);
        }
    };

    checkActiveStatus = async () => {
        await this.props.api.call({
            method: 'get',
            url: '/security/status/check',
        });
    };

    updateActiveStatus = async () => {
        await this.props.api.call({
            method: 'get',
            url: '/security/status/ping',
        });
    };

    logout = async () => {
        try {
            await this.props.api.call({
                method: 'post',
                url: '/logout',
            });
        } catch (error) {
            // Log them out anyway
        } finally {
            this.onLogout();
        }
    };

    onLogout() {
        this.props.auth.setAuth(false);
        this.state.user = null;
        this.initUserRolesMap();
        this.props.events.publish(this.events.userUpdated);
        this.props.events.publish(this.events.userLoggedOut);
    }

    requestReset = async (email) => {
        try {
            const token = await this.props.api.call(
                {
                    method: 'get',
                    url: '/reset-password/request',
                },
                { handledErrors: true }
            );
            const result = await this.props.api.call({
                method: 'post',
                url: '/reset-password/request',
                data: {
                    _email: email,
                    _csrf_token: token.data.csrf_token,
                },
            });
            return result.data.message;
        } catch (error) {
            if (error.response && error.response.data) {
                throw new Error(error.response.data.message);
            }
            throw new Error('An error occurred.');
        }
    };

    setResetEndpoint = (endpoint) => {
        this.state.reset_endpoint = endpoint;
    };

    getResetEndpoint = () => this.state.reset_endpoint;

    passwordReset = async (password) => {
        try {
            const result = await this.props.api.call(
                {
                    method: 'post',
                    url: this.getResetEndpoint(),
                    data: { _password: password },
                },
                { handledErrors: true }
            );
            return result.data.message;
        } catch (error) {
            throw defaultApiResponseHandler(error);
        }
    };

    update = async (profile, options) => {
        try {
            const result = await this.props.api.call(
                {
                    method: 'put',
                    url: this.state.user['@id'],
                    data: profile,
                },
                options
            );
            this.state.user = result.data;
            this.initUserRolesMap();
            this.props.events.publish(this.events.userUpdated);
            return true;
        } catch (error) {
            throw defaultApiResponseHandler(error);
        }
    };

    setPhone = async (userPhone) => {
        const phoneId = userPhone.id;
        let result;
        if (phoneId) {
            result = await this.props.api.call({
                method: 'put',
                url: `/api/user-phones/${phoneId}`,
                data: {
                    send_verification_code: true,
                    phone: { number: userPhone.phone.number },
                    _csrf_token: this.props.api.csrf_token,
                },
            });
        } else {
            result = await this.props.api.call({
                method: 'post',
                url: `/api/user-phones`,
                data: {
                    send_verification_code: true,
                    user: this.state.two_factor_auth_enrollment?.user || { id: this.state.user.id },
                    phone: userPhone.phone,
                    _csrf_token: this.props.api.csrf_token,
                },
            });
        }
        if (this.state.user?.id) {
            this.state.user.user_phones = this.state.user.user_phones.filter(
                (phone) => phone.id !== result.data.id
            );
            this.state.user.user_phones.push(result.data);
            this.props.events.publish(this.events.userUpdated);
        }
        return result.data;
    };

    deletePhone = async (userPhoneId) => {
        await this.props.api.call({
            method: 'delete',
            url: `/api/user-phones/${userPhoneId}`,
            data: {
                _csrf_token: this.props.api.csrf_token,
            },
        });
        if (this.state.user?.id) {
            this.state.user.user_phones = this.state.user.user_phones.filter(
                (x) => x.id !== userPhoneId
            );
            this.props.events.publish(this.events.userUpdated);
        }
    };

    verifyPhone = async (code, userPhoneId) => {
        try {
            const result = await this.props.api.call(
                {
                    method: 'post',
                    url: `/api/user-phones/${userPhoneId}/verify`,
                    data: {
                        code,
                        _csrf_token: this.props.api.csrf_token,
                    },
                },
                { handledErrors: true }
            );
            this.state.user = result.data.user;
            this.props.events.publish(this.events.userUpdated);
            return true;
        } catch (error) {
            throw defaultApiResponseHandler(error);
        }
    };

    deleteGoogleAuth = async () => {
        await this.props.api.call({
            method: 'delete',
            url: `/api/totp-authenticators`,
            data: {
                provider: 'google',
                user_id: this.state.user.id,
            },
        });
        this.state.user.two_factor_auth.is_google_authenticator_enabled = false;
        this.props.events.publish(this.events.userUpdated);
    };

    getSavedSearchData() {
        const savedFilters = this.state.user.saved_search_data?.saved_filters
            ? this.state.user.saved_search_data.saved_filters.sort((a, b) =>
                  a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1
              )
            : [];
        const savedColumnPresets = this.state.user.saved_search_data?.column_presets
            ? this.state.user.saved_search_data.column_presets.sort((a, b) =>
                  a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1
              )
            : [];
        const savedPatientPrintOptions = this.state.user.saved_search_data?.patient_print_options;
        const savedNotificationOptions = this.state.user.saved_search_data?.notification_options;
        return {
            savedFilters,
            savedColumnPresets,
            savedPatientPrintOptions,
            savedNotificationOptions,
        };
    }

    // property is saved_filters or column_presets
    addSavedSearchData = async (property, value) => {
        if (this.state.user.saved_search_data[property].some((x) => x.label === value.label)) {
            throw new Error('Duplicate name');
        }
        this.state.user.saved_search_data[property].push(value);
        await this.updateSavedSearchData();
    };

    removeSavedSearchData = async (property, value) => {
        this.state.user.saved_search_data[property] = this.state.user.saved_search_data[
            property
        ].filter((x) => x.label !== value);
        await this.updateSavedSearchData();
    };

    setSavedSearchData = async (property, value) => {
        this.state.user.saved_search_data[property] = value;
        await this.updateSavedSearchData();
    };

    updateSavedSearchData = async () => {
        await this.props.api.call({
            method: 'put',
            url: this.state.user['@id'],
            data: { saved_search_data: this.state.user.saved_search_data },
        });
        this.props.events.publish(this.events.savedSearchDataUpdated);
        return true;
    };

    dismissBanner = async (id) => {
        await this.props.api.call({
            method: 'post',
            url: `/api/banners/${id}/dismiss`,
        });
    };

    getExternalAccount = (accountType) => {
        return this.state.user.user_external_accounts?.find(
            (x) => enums(['user', 'external_account_types', x.type]) === accountType
        );
    };

    disableLRLink = async () => {
        const linkId = this.getExternalAccount('Core App')?.id;
        await this.props.api.call({
            method: 'delete',
            url: `/api/user-external-accounts/${linkId}`,
        });
        this.state.user.user_external_accounts = this.state.user.user_external_accounts.filter(
            (x) => x.id !== linkId
        );
        this.props.events.publish(this.events.userUpdated);
    };

    hasFeatureToggle = (feature) => {
        if (window.app.feature_toggles?.[feature] === 2) {
            return this.isGlobalAdmin();
        }
        if (window.app.feature_toggles?.[feature] === 3) {
            return !this.isGlobalAdmin();
        }
        return !!window.app.feature_toggles?.[feature];
    };
}

export { UserService };
